/**
 *
 * @module adobeIdentityApi
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Account from './../../account/entry';
import Token from './../../token/entry';
import Exception from './../../services/exception/entry';
import AdobeIdentityManager from './adobeIdentityManager';

const AccountManager = Account.AccountManager;
const TokenManager = Token.TokenManager;

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class AdobeIdentityApi
 * @desc Provides a way to get an Adobe identity activated.
 *
 */
export default class AdobeIdentityApi {

    /**
     *
     * @param {AdobeIdentityManager} adobeIdentityManager
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Account.AccountManager} accountManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(adobeIdentityManager, tokenManager, accountManager, logger = Logging) {

        const errorMessage = {
            ctor: 'AdobeIdentityApi(adobeIdentityManager, tokenManager, accountManager, logger)',
            get adobeIdentityManager() {
                return `${this.ctor} needs AdobeIdentityManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get accountManager() {
                return `${this.ctor} needs SDK.Account.AccountManager instance`;
            }
        };

        instanceStrict(adobeIdentityManager, AdobeIdentityManager, errorMessage.adobeIdentityManager, InvalidArgumentException); // eslint-disable-line
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(accountManager, AccountManager, errorMessage.accountManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {AdobeIdentityManager}
         *
         */
        this.adobeIdentityManager = adobeIdentityManager;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Account.AccountManager}
         *
         */
        this.accountManager = accountManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        this.logger.info(AdobeIdentityApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @desc Gets an AdobeRegistration instance with a code that can be
     * used to activate the identity.
     * @returns {Promise<AdobeRegistration>} Returns an AdobeRegistration that can
     * be passed to AdobeIdentityApi#activateRegistration to activate the identity.
     *
     */
    getRegistration() {

        return this.adobeIdentityManager.getRegistration();

    }

    /**
     *
     * @access public
     * @param {AdobeRegistration} adobeRegistration - The AdobeRegistration that
     * was generated for the identity.
     * @param {String} adobeId - The ID of the MVPD the identity is activating with.
     * @desc Activates the Adobe identity.
     * @returns {Promise<AdobeActivationResult>} Returns an asynchronous action that will
     * send a notification when it is complete
     *
     */
    activateRegistration(adobeRegistration, adobeId) {

        return this.adobeIdentityManager.activateRegistration(adobeRegistration, adobeId);

    }

}



// WEBPACK FOOTER //
// ./src/identity/adobe/adobeIdentityApi.js